import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import { HTTPService } from "../../../services/httpService";
import ServiceCard from './ServiceCard';
import TimeClockHeader from './TimeClockHeader';
import ItemMaterialCaptureModal from '../../partial/ItemMaterialCaptureModal';
import EnforcementModal from '../../common/EnforcementModal';
import { getItems, createItem, completeItemWithMaterials, updateItem } from '../../../services/serviceItemsService';
import { getServiceItemTemplates } from '../../../services/servicesApiV2';
import { startService, finishService, updateRequestState, updateRequestOfferState, addAuditRecord, sendSmsNotification } from '../../../util/serviceActions';
import { checkIn } from '../../../services/timeTrackingService';
import { getCurrentPosition, GeolocationError } from '../../../util/geoUtils';
import moment from 'moment';

const API = new HTTPService();

/**
 * MyWork - Simplified work view for technicians
 *
 * Shows services assigned to the current technician, grouped by status:
 * In Progress (blue), Up Next (amber), Completed Today (green).
 * Technicians can start/finish their assigned work and track progress.
 *
 * Props (from App.js):
 * - user: Current user object
 * - typeId: Company/installer ID
 * - companyName: Company name
 */
class MyWork extends Component {
  state = {
    // Data
    assignedServices: [],
    loading: true,
    error: null,

    // UI State
    expandedServiceId: null, // Which service card is expanded (request_id:service_id)

    // Materials Collection
    categoryConfigs: {},

    // Materials View Modal
    showMaterialsViewModal: false,
    serviceForMaterialsView: null, // Service whose materials are being viewed

    // Technician Notes Modal
    showNotesModal: false,
    serviceForNotes: null, // Service being edited
    notesText: '',
    savingNotes: false,

    // Action state
    actionInProgress: null, // 'start' | 'finish' | null

    // Time Tracking
    isCheckedIn: false,
    currentSession: null,
    timeTrackingEnabled: false, // Whether time tracking feature is enabled for this location
    timeTrackingRequired: false, // Whether check-in is required before starting work
    locationId: null,

    // Enforcement Modal
    showEnforcementModal: false,
    pendingAction: null, // Service waiting for check-in before action
    enforcementCheckInLoading: false,

    // Service Items (Phase 2)
    serviceItems: {},           // { [requestServiceId]: Array } — cached items per service
    showItemCaptureModal: false,
    itemCaptureService: null,   // Service object for the modal
    itemCaptureItems: []        // Material items array for the modal
  };

  async componentDidMount() {
    await this.loadMyWork();
    await this.loadCategoryConfigs();
    await this.loadLocationSettings();
  }

  loadMyWork = async () => {
    try {
      this.setState({ loading: true, error: null });

      const { user } = this.props;
      if (!user?.User_id) {
        this.setState({ error: 'User not loaded', loading: false });
        return;
      }

      // Fetch services assigned to this technician
      const { data: services } = await API.get(`/requestService/technician/${user.User_id}`);

      this.setState({
        assignedServices: services || [],
        loading: false
      });
    } catch (error) {
      console.error('Error loading my work:', error);
      this.setState({
        error: 'Failed to load your assigned work',
        loading: false
      });
    }
  };

  loadCategoryConfigs = async () => {
    try {
      const { data: configs } = await API.get('/serviceCategoryConfig');
      const categoryConfigs = {};
      if (Array.isArray(configs)) {
        configs.forEach(config => {
          categoryConfigs[config.category_name] = config;
        });
      }
      this.setState({ categoryConfigs });
    } catch (error) {
      console.error("Error loading category configs:", error);
    }
  };

  // Load location/time tracking settings from company data
  loadLocationSettings = async () => {
    try {
      const { typeId } = this.props;
      const companyData = JSON.parse(localStorage.getItem('companyAPI') || '{}');

      // Time tracking mode: 'none' | 'voluntary' | 'required'
      // - none: feature disabled, hide UI
      // - voluntary: feature enabled, check-in optional
      // - required: feature enabled, must check-in before working
      const timeTrackingMode = companyData.time_tracking_mode || 'none';
      const timeTrackingEnabled = timeTrackingMode !== 'none';
      const timeTrackingRequired = timeTrackingMode === 'required';

      // For now, location maps 1:1 with installer
      this.setState({
        locationId: typeId,
        timeTrackingEnabled,
        timeTrackingRequired
      });
    } catch (error) {
      console.error("Error loading location settings:", error);
    }
  };

  // Handle time tracking status change from TimeClockHeader
  handleTimeTrackingStatusChange = (isCheckedIn, session) => {
    this.setState({
      isCheckedIn,
      currentSession: session
    });

    // If check-in was successful and we have a pending action, execute it
    if (isCheckedIn && this.state.pendingAction) {
      const service = this.state.pendingAction;
      this.setState({ pendingAction: null, showEnforcementModal: false });
      this.handleStartService(service);
    }
  };

  // Handle enforcement modal check-in
  handleEnforcementCheckIn = async () => {
    const { locationId } = this.state;
    const { user } = this.props;

    try {
      this.setState({ enforcementCheckInLoading: true });

      // Get GPS position
      let gpsData = { lat: null, lng: null, accuracy_m: null };
      try {
        gpsData = await getCurrentPosition({ timeout: 15000 });
      } catch (gpsErr) {
        if (gpsErr instanceof GeolocationError && gpsErr.code === 'PERMISSION_DENIED') {
          alert('Location access is required to check in. Please enable location services.');
          this.setState({ enforcementCheckInLoading: false });
          return;
        }
      }

      // Attempt check-in
      const result = await checkIn({
        location_id: locationId,
        user_id: user.User_id,
        lat: gpsData.lat,
        lng: gpsData.lng,
        accuracy_m: gpsData.accuracy_m,
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          timestamp: new Date().toISOString()
        }
      });

      if (result.success) {
        // Update state and proceed with pending action
        const pendingService = this.state.pendingAction;
        this.setState({
          isCheckedIn: true,
          currentSession: result.session,
          enforcementCheckInLoading: false,
          showEnforcementModal: false,
          pendingAction: null
        });

        // Execute the pending action
        if (pendingService) {
          this.handleStartService(pendingService);
        }
      } else {
        // Handle blocked check-in
        let errorMsg = result.message || 'Check-in failed';
        if (result.reason_code === 'outside_radius') {
          errorMsg = `You are ${result.distance_ft || '?'} feet from the shop. Please move within ${result.required_radius_ft || '?'} feet to check in.`;
        } else if (result.reason_code === 'accuracy_too_low') {
          errorMsg = `GPS accuracy is ${result.accuracy_m || '?'}m. Please wait for better signal.`;
        }
        alert(errorMsg);
        this.setState({ enforcementCheckInLoading: false });
      }
    } catch (err) {
      console.error('Enforcement check-in error:', err);
      const errorData = err.response?.data;
      alert(errorData?.message || 'Check-in failed. Please try again.');
      this.setState({ enforcementCheckInLoading: false });
    }
  };

  // Close enforcement modal
  handleCloseEnforcementModal = () => {
    this.setState({
      showEnforcementModal: false,
      pendingAction: null
    });
  };

  // Toggle expanded service card — also loads items if not cached
  handleToggleExpand = (service) => {
    const cardId = `${service.request_id}:${service.service_id}`;
    const isExpanding = this.state.expandedServiceId !== cardId;

    this.setState(prevState => ({
      expandedServiceId: prevState.expandedServiceId === cardId ? null : cardId
    }));

    // Load items when expanding, if not already cached
    if (isExpanding && service.id && !this.state.serviceItems[service.id]) {
      this.loadServiceItems(service.id);
    }
  };

  // Load items for a service and cache them
  // Also loads work step templates from master service if no job-level work items exist
  loadServiceItems = async (requestServiceId) => {
    try {
      console.log('[MyWork] loadServiceItems called for requestServiceId:', requestServiceId);

      // Load job-level items first
      let jobItems = await getItems(requestServiceId, { silent: true }) || [];
      console.log('[MyWork] jobItems RAW from server:', JSON.stringify(jobItems, null, 2));

      // Find the service to get master_service_id
      const service = this.state.assignedServices.find(s => s.id === requestServiceId);
      const masterServiceId = service?.master_service_id || service?.installer_service_id || service?.service_id;
      console.log('[MyWork] using masterServiceId:', masterServiceId);

      // Load templates to help with type inference and to show missing work steps
      let templates = [];
      if (masterServiceId) {
        try {
          templates = await getServiceItemTemplates(masterServiceId) || [];
          console.log('[MyWork] templates RAW from server:', JSON.stringify(templates, null, 2));
        } catch (templateErr) {
          console.log('[MyWork] Error loading templates:', templateErr);
        }
      }

      // Build maps for type lookup
      const templateById = {};
      const workTemplateLabels = new Set();
      templates.forEach(t => {
        templateById[t.id] = t;
        if (t.type === 'work') {
          workTemplateLabels.add(t.label.toLowerCase().trim());
        }
      });

      // Infer type for job items that don't have type set
      jobItems = jobItems.map(item => {
        if (item.type) {
          console.log(`[MyWork] Item "${item.label}" already has type: ${item.type}`);
          return item;
        }

        // Try to infer from service_task_id first
        if (item.service_task_id && templateById[item.service_task_id]) {
          const inferredType = templateById[item.service_task_id].type;
          console.log(`[MyWork] Item "${item.label}" inferred type from service_task_id ${item.service_task_id}: ${inferredType}`);
          return { ...item, type: inferredType };
        }

        // Fallback: match by label - if label matches a work template, it's a work item
        if (item.label && workTemplateLabels.has(item.label.toLowerCase().trim())) {
          console.log(`[MyWork] Item "${item.label}" inferred type from label match: work`);
          return { ...item, type: 'work' };
        }

        console.log(`[MyWork] Item "${item.label}" has no type and no match found`);
        return item;
      });

      console.log('[MyWork] jobItems AFTER type inference:', jobItems.map(i => ({ id: i.id, label: i.label, type: i.type, status: i.status, service_task_id: i.service_task_id })));

      // Build sets for deduplication - both by template ID and by label
      const jobItemTemplateIds = new Set(
        jobItems.filter(i => i.service_task_id).map(i => i.service_task_id)
      );
      const jobItemLabels = new Set(
        jobItems.map(i => (i.label || '').toLowerCase().trim())
      );

      // Get work templates that don't yet have job-level items (check both ID and label)
      const workTemplates = templates.filter(t =>
        t.type === 'work' &&
        !jobItemTemplateIds.has(t.id) &&
        !jobItemLabels.has((t.label || '').toLowerCase().trim())
      );
      console.log('[MyWork] workTemplates without job items:', workTemplates);

      // Convert remaining templates to virtual items
      const virtualWorkItems = workTemplates.map(t => ({
        id: `template-${t.id}`,
        label: t.label,
        type: 'work',
        status: 'pending',
        _fromTemplate: true,
        _templateId: t.id,
        _requestServiceId: requestServiceId
      }));

      const allItems = [...jobItems, ...virtualWorkItems];

      // Log what will show in each section
      const materialItems = allItems.filter(i => !i.type || i.type === 'material');
      const workItems = allItems.filter(i => i.type === 'work');
      console.log('[MyWork] FINAL - Material items (Preferred Products):', materialItems.map(i => ({ id: i.id, label: i.label, type: i.type })));
      console.log('[MyWork] FINAL - Work items (Work Steps):', workItems.map(i => ({ id: i.id, label: i.label, type: i.type, status: i.status })));

      this.setState(prevState => ({
        serviceItems: {
          ...prevState.serviceItems,
          [requestServiceId]: allItems
        }
      }));
    } catch (error) {
      console.error('[MyWork] Error loading service items:', error);
    }
  };

  // Toggle item complete/pending
  handleToggleItem = async (item) => {
    try {
      if (item.status !== 'completed') {
        // Mark complete
        if (item._fromTemplate) {
          // Template item: create job-level item first, then complete it
          const requestServiceId = item._requestServiceId;
          if (!requestServiceId) {
            console.error('No requestServiceId for template item');
            return;
          }

          // Create the job-level item
          const newItem = await createItem(requestServiceId, {
            label: item.label,
            type: 'work',
            service_task_id: item._templateId  // Link to template
          });

          // Mark it complete
          await completeItemWithMaterials(newItem.id);

          // Update local state: replace template item with job-level item
          // Preserve label and type in case server doesn't return them
          this.updateLocalTemplateItem(item, {
            ...newItem,
            label: item.label,  // Preserve original label
            type: 'work',
            status: 'completed'
          });
        } else {
          // Job-level item: just complete it
          await completeItemWithMaterials(item.id);
          this.updateLocalItemStatus(item, 'completed');
        }
      } else {
        // Mark pending
        await updateItem(item.id, { status: 'pending' });
        this.updateLocalItemStatus(item, 'pending');
      }
    } catch (error) {
      console.error('Error toggling item:', error);
      alert('Error updating item. Please try again.');
    }
  };

  // Replace a template item with a job-level item in the cache
  updateLocalTemplateItem = (templateItem, jobItem) => {
    this.setState(prevState => {
      const serviceItems = { ...prevState.serviceItems };
      for (const [rsId, items] of Object.entries(serviceItems)) {
        const idx = items.findIndex(i => i.id === templateItem.id);
        if (idx !== -1) {
          serviceItems[rsId] = items.map(i =>
            i.id === templateItem.id ? jobItem : i
          );
          break;
        }
      }
      return { serviceItems };
    });
  };

  // Update a single item's status in the local serviceItems cache
  updateLocalItemStatus = (item, newStatus) => {
    this.setState(prevState => {
      const serviceItems = { ...prevState.serviceItems };
      // Find which requestServiceId this item belongs to
      for (const [rsId, items] of Object.entries(serviceItems)) {
        const idx = items.findIndex(i => i.id === item.id);
        if (idx !== -1) {
          serviceItems[rsId] = items.map(i =>
            i.id === item.id ? { ...i, status: newStatus } : i
          );
          break;
        }
      }
      return { serviceItems };
    });
  };

  // Show materials used for a service
  handleShowMaterials = (service) => {
    this.setState({
      showMaterialsViewModal: true,
      serviceForMaterialsView: service
    });
  };

  // Hide materials view modal
  handleHideMaterialsView = () => {
    this.setState({
      showMaterialsViewModal: false,
      serviceForMaterialsView: null
    });
  };

  // Start a service
  handleStartService = async (service) => {
    console.log('🚀 handleStartService called with:', service);

    // Check if time tracking enforcement is required
    const { timeTrackingRequired, isCheckedIn } = this.state;
    if (timeTrackingRequired && !isCheckedIn) {
      // Show enforcement modal and store pending action
      this.setState({
        showEnforcementModal: true,
        pendingAction: service
      });
      return;
    }

    try {
      this.setState({ actionInProgress: 'start' });

      const { user, typeId } = this.props;
      console.log('🔧 Calling startService with:', {
        requestId: service.request_id,
        serviceId: service.service_id,
        installerId: parseInt(typeId),
        userId: user.User_id,
        userType: 'installer'
      });

      await startService({
        requestServiceId: service.id,
        requestId: service.request_id,
        serviceId: service.service_id,
        installerId: parseInt(typeId),
        userId: user.User_id,
        userType: 'installer'
      });

      console.log('✅ startService completed successfully');

      // If this is the first service being started on the job (request is in "received" state),
      // update the request state to "started"
      if (service.current_state === 'received') {
        await updateRequestState({
          requestId: service.request_id,
          currentState: 'started'
        });

        await updateRequestOfferState({
          requestId: service.request_id,
          installerId: parseInt(typeId),
          currentStatus: 'started'
        });

        // Add audit record for the job state change
        await addAuditRecord({
          requestId: service.request_id,
          status: 'started',
          serviceId: null,
          userId: user.User_id,
          userType: 'installer'
        });

        // Send SMS notification to customer/dealer contact (non-blocking)
        const { companyName } = this.props;
        try {
          if (service.request_type === 'dealer') {
            // For dealer jobs, notify the dealer contact person
            const contactId = service.dealer_contact || service.dealerContact || service.requestor;
            await sendSmsNotification({
              messageType: 'User',
              userType: 'dealer',
              recipientId: contactId,
              text: `${companyName} has started work on your vehicle`
            });
          } else {
            // For retail/customer jobs, notify the customer on record
            await sendSmsNotification({
              messageType: 'Customer',
              userType: 'customer',
              recipientId: service.requestor,
              text: `${companyName} has started work on your vehicle`
            });
          }
        } catch (smsError) {
          console.warn('Failed to send SMS notification:', smsError);
        }
      }

      // Update local state
      this.setState(prevState => ({
        assignedServices: prevState.assignedServices.map(s =>
          (s.request_id === service.request_id && s.service_id === service.service_id)
            ? { ...s, service_state: 'started', started_at: new Date().toISOString(), started_by: user.User_id, current_state: 'started' }
            : s
        ),
        actionInProgress: null
      }));

      alert('Service started!');
    } catch (error) {
      console.error('Error starting service:', error);
      alert('Error starting service. Please try again.');
      this.setState({ actionInProgress: null });
    }
  };

  // Finish a service - check if materials collection needed
  handleFinishService = async (service) => {
    const { serviceItems } = this.state;

    // Check shop-level materials tracking setting
    const companyData = JSON.parse(localStorage.getItem('companyAPI') || '{}');
    const materialsTrackingEnabled = companyData?.materials_tracking_enabled !== false;

    // Check if this service requires data collection (installer-level flag)
    const serviceRequiresDataCollect = service.data_collect_required;

    // Check if service has incomplete material tasks
    const items = serviceItems[service.id] || [];
    const materialItems = items.filter(i => !i.type || i.type === 'material');
    const incompleteTasks = materialItems.filter(t => t.status !== 'completed' && t.status !== 'skipped');

    // If service requires data collection AND has incomplete material tasks, show per-item capture
    if (materialsTrackingEnabled && serviceRequiresDataCollect && incompleteTasks.length > 0) {
      this.setState({
        showItemCaptureModal: true,
        itemCaptureService: service,
        itemCaptureItems: incompleteTasks
      });
      return;
    }

    // No materials needed (or all already done), complete directly
    await this.completeFinishService(service);
  };

  // Handle per-item material capture completion
  handleItemCaptureComplete = () => {
    const { itemCaptureService } = this.state;
    this.setState({
      showItemCaptureModal: false,
      itemCaptureService: null,
      itemCaptureItems: []
    });
    if (itemCaptureService) {
      this.completeFinishService(itemCaptureService);
    }
  };

  // Complete the finish action (called after materials collection or directly)
  completeFinishService = async (service) => {
    try {
      this.setState({ actionInProgress: 'finish' });

      const { user, typeId } = this.props;

      await finishService({
        requestServiceId: service.id,
        requestId: service.request_id,
        serviceId: service.service_id,
        installerId: parseInt(typeId),
        userId: user.User_id,
        userType: 'installer'
      });

      // Update local state
      this.setState(prevState => ({
        assignedServices: prevState.assignedServices.map(s =>
          (s.request_id === service.request_id && s.service_id === service.service_id)
            ? { ...s, service_state: 'finished', finished_at: new Date().toISOString(), finished_by: user.User_id }
            : s
        ),
        actionInProgress: null
      }));

      alert('Service completed!');
    } catch (error) {
      console.error('Error finishing service:', error);
      alert('Error completing service. Please try again.');
      this.setState({ actionInProgress: null });
    }
  };

  // =========================================================================
  // Technician Notes Handlers
  // =========================================================================

  // Show notes modal
  handleShowNotes = (service) => {
    this.setState({
      showNotesModal: true,
      serviceForNotes: service,
      notesText: service.technician_instructions || ''
    });
  };

  // Hide notes modal
  handleHideNotes = () => {
    this.setState({
      showNotesModal: false,
      serviceForNotes: null,
      notesText: ''
    });
  };

  // Handle notes text change
  handleNotesChange = (text) => {
    this.setState({ notesText: text });
  };

  // Save notes
  handleSaveNotes = async () => {
    const { serviceForNotes, notesText } = this.state;
    if (!serviceForNotes) return;

    try {
      this.setState({ savingNotes: true });

      // Save to backend
      const patchUrl = serviceForNotes.id
        ? `/requestService/row/${serviceForNotes.id}`
        : `/requestService/${serviceForNotes.request_id}/${serviceForNotes.service_id}`;
      await API.patch(patchUrl, {
        technician_instructions: notesText
      });

      // Update local state
      this.setState(prevState => ({
        assignedServices: prevState.assignedServices.map(s =>
          (serviceForNotes.id ? s.id === serviceForNotes.id : (s.request_id === serviceForNotes.request_id && s.service_id === serviceForNotes.service_id))
            ? { ...s, technician_instructions: notesText }
            : s
        ),
        showNotesModal: false,
        serviceForNotes: null,
        notesText: '',
        savingNotes: false
      }));

    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Error saving notes. Please try again.');
      this.setState({ savingNotes: false });
    }
  };

  // Group services by status: In Progress, Up Next, Completed Today
  groupServicesByStatus = (services) => {
    const today = moment().startOf('day');
    const tomorrow = moment().add(1, 'day').startOf('day');

    const inProgress = [];
    const upNext = [];
    const completed = [];

    services.forEach(service => {
      if (service.service_state === 'started') {
        inProgress.push(service);
      } else if (service.service_state === 'finished') {
        // Only show today's completed work
        if (!service.work_date) {
          completed.push(service); // unscheduled completed — keep for visibility
        } else {
          const workDate = moment(service.work_date).startOf('day');
          if (!workDate.isBefore(today)) {
            completed.push(service);
          }
        }
      } else {
        // new / booked → Up Next
        upNext.push(service);
      }
    });

    // Sort In Progress: oldest started_at first (longest-running on top)
    inProgress.sort((a, b) => {
      const aTime = a.started_at ? moment(a.started_at).valueOf() : 0;
      const bTime = b.started_at ? moment(b.started_at).valueOf() : 0;
      return aTime - bTime;
    });

    // Sort Up Next: past due first, then today, then future by date, then unscheduled
    upNext.sort((a, b) => {
      const aDate = a.work_date ? moment(a.work_date).startOf('day') : null;
      const bDate = b.work_date ? moment(b.work_date).startOf('day') : null;
      const aPastDue = aDate && aDate.isBefore(today) ? 1 : 0;
      const bPastDue = bDate && bDate.isBefore(today) ? 1 : 0;

      // Past due items first
      if (aPastDue !== bPastDue) return bPastDue - aPastDue;
      // Unscheduled items last
      if (!aDate && bDate) return 1;
      if (aDate && !bDate) return -1;
      if (!aDate && !bDate) return 0;
      // By date ascending
      return aDate.valueOf() - bDate.valueOf();
    });

    // Sort Completed: most recent finished_at first
    completed.sort((a, b) => {
      const aTime = a.finished_at ? moment(a.finished_at).valueOf() : 0;
      const bTime = b.finished_at ? moment(b.finished_at).valueOf() : 0;
      return bTime - aTime;
    });

    // Compute dateTag for upNext items
    upNext.forEach(service => {
      if (!service.work_date) {
        service._dateTag = 'Unscheduled';
      } else {
        const workDate = moment(service.work_date).startOf('day');
        if (workDate.isBefore(today)) {
          service._dateTag = 'Past Due';
        } else if (workDate.isSame(today, 'day')) {
          service._dateTag = 'Today';
        } else if (workDate.isSame(tomorrow, 'day')) {
          service._dateTag = 'Tomorrow';
        } else {
          service._dateTag = workDate.format('MMM D');
        }
      }
    });

    return { inProgress, upNext, completed };
  };

  // Get summary stats (only counts today's completed work, not past days)
  getStats = () => {
    const { assignedServices } = this.state;
    const today = moment().startOf('day');
    const notStarted = assignedServices.filter(s => s.service_state === 'new' || s.service_state === 'booked').length;
    const inProgress = assignedServices.filter(s => s.service_state === 'started').length;
    const completed = assignedServices.filter(s => {
      if (s.service_state !== 'finished') return false;
      if (!s.work_date) return true;
      return !moment(s.work_date).startOf('day').isBefore(today);
    }).length;
    const total = notStarted + inProgress + completed;

    return { total, notStarted, inProgress, completed };
  };

  render() {
    const {
      assignedServices,
      loading,
      error,
      expandedServiceId,
      categoryConfigs,
      actionInProgress,
      showMaterialsViewModal,
      serviceForMaterialsView,
      showNotesModal,
      serviceForNotes,
      notesText,
      savingNotes,
      // Time tracking state
      isCheckedIn,
      timeTrackingEnabled,
      timeTrackingRequired,
      locationId,
      showEnforcementModal,
      enforcementCheckInLoading,
      // Service items
      serviceItems,
      showItemCaptureModal,
      itemCaptureService,
      itemCaptureItems
    } = this.state;

    const { user, typeId } = this.props;
    const stats = this.getStats();
    const { inProgress, upNext, completed } = this.groupServicesByStatus(assignedServices);

    return (
      <div className="container-fluid mt-4">
        {/* Time Clock Header - Check In/Out (only shown if time tracking is enabled) */}
        {timeTrackingEnabled && locationId && user?.User_id && (
          <TimeClockHeader
            locationId={locationId}
            userId={user.User_id}
            onStatusChange={this.handleTimeTrackingStatusChange}
          />
        )}

        {/* Header */}
        <div className="row mb-4">
          <div className="col">
            <h4>My Work</h4>
            <p className="text-muted mb-0">
              {stats.total === 0 ? (
                'No services assigned to you'
              ) : (
                <>
                  {stats.notStarted > 0 && `${stats.notStarted} to do`}
                  {stats.notStarted > 0 && stats.inProgress > 0 && ' | '}
                  {stats.inProgress > 0 && `${stats.inProgress} in progress`}
                  {(stats.notStarted > 0 || stats.inProgress > 0) && stats.completed > 0 && ' | '}
                  {stats.completed > 0 && `${stats.completed} completed`}
                </>
              )}
            </p>
          </div>
          <div className="col-auto">
            <button
              className="btn btn-outline-secondary"
              onClick={this.loadMyWork}
              disabled={loading}
            >
              <i className={`fa-sharp fa-regular fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="alert alert-danger">
            {error}
            <button className="btn btn-link" onClick={this.loadMyWork}>
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading your work...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && assignedServices.length === 0 && (
          <div className="text-center py-5">
            <i className="fa-sharp fa-regular fa-check-circle fa-3x text-success mb-3"></i>
            <h5>All caught up!</h5>
            <p className="text-muted">
              You don't have any services assigned to you right now.
            </p>
          </div>
        )}

        {/* Service List by Status */}
        {!loading && !error && assignedServices.length > 0 && (
          <div className="services-list">
            {/* In Progress Panel */}
            {inProgress.length > 0 && (
              <div className="card mb-4" style={{ borderTop: '3px solid #007bff' }}>
                <div className="card-header d-flex align-items-center" style={{ backgroundColor: '#e7f1ff' }}>
                  <i className="fa-sharp fa-solid fa-spinner text-primary mr-2"></i>
                  <strong className="text-primary">In Progress</strong>
                  <span className="badge badge-primary ml-2">{inProgress.length}</span>
                </div>
                <div className="card-body pb-1">
                  {inProgress.map(service => {
                    const cardId = `${service.request_id}:${service.service_id}`;
                    return (
                      <ServiceCard
                        key={cardId}
                        service={service}
                        expanded={expandedServiceId === cardId}
                        onToggle={() => this.handleToggleExpand(service)}
                        onStart={() => this.handleStartService(service)}
                        onFinish={() => this.handleFinishService(service)}
                        actionInProgress={actionInProgress}
                        onShowMaterials={this.handleShowMaterials}
                        onAddNotes={this.handleShowNotes}
                        timeTrackingRequired={timeTrackingRequired}
                        isCheckedIn={isCheckedIn}
                        items={serviceItems[service.id] || []}
                        onToggleItem={this.handleToggleItem}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Up Next Panel */}
            {upNext.length > 0 && (
              <div className="card mb-4" style={{ borderTop: '3px solid #ffc107' }}>
                <div className="card-header d-flex align-items-center" style={{ backgroundColor: '#fff8e1' }}>
                  <i className="fa-sharp fa-solid fa-clock text-warning mr-2"></i>
                  <strong style={{ color: '#856404' }}>Up Next</strong>
                  <span className="badge badge-warning ml-2">{upNext.length}</span>
                </div>
                <div className="card-body pb-1">
                  {upNext.map(service => {
                    const cardId = `${service.request_id}:${service.service_id}`;
                    return (
                      <ServiceCard
                        key={cardId}
                        service={service}
                        expanded={expandedServiceId === cardId}
                        onToggle={() => this.handleToggleExpand(service)}
                        onStart={() => this.handleStartService(service)}
                        onFinish={() => this.handleFinishService(service)}
                        actionInProgress={actionInProgress}
                        onShowMaterials={this.handleShowMaterials}
                        onAddNotes={this.handleShowNotes}
                        timeTrackingRequired={timeTrackingRequired}
                        isCheckedIn={isCheckedIn}
                        items={serviceItems[service.id] || []}
                        onToggleItem={this.handleToggleItem}
                        dateTag={service._dateTag}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Completed Today Panel */}
            {completed.length > 0 && (
              <div className="card mb-4" style={{ borderTop: '3px solid #28a745' }}>
                <div className="card-header d-flex align-items-center" style={{ backgroundColor: '#e8f5e9' }}>
                  <i className="fa-sharp fa-solid fa-check-circle text-success mr-2"></i>
                  <strong className="text-success">Completed Today</strong>
                  <span className="badge badge-success ml-2">{completed.length}</span>
                </div>
                <div className="card-body pb-1">
                  {completed.map(service => {
                    const cardId = `${service.request_id}:${service.service_id}`;
                    return (
                      <ServiceCard
                        key={cardId}
                        service={service}
                        expanded={expandedServiceId === cardId}
                        onToggle={() => this.handleToggleExpand(service)}
                        onStart={() => this.handleStartService(service)}
                        onFinish={() => this.handleFinishService(service)}
                        actionInProgress={actionInProgress}
                        onShowMaterials={this.handleShowMaterials}
                        onAddNotes={this.handleShowNotes}
                        timeTrackingRequired={timeTrackingRequired}
                        isCheckedIn={isCheckedIn}
                        items={serviceItems[service.id] || []}
                        onToggleItem={this.handleToggleItem}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Per-Item Material Capture Modal */}
        {showItemCaptureModal && itemCaptureService && (
          <ItemMaterialCaptureModal
            show={showItemCaptureModal}
            onHide={() => this.setState({ showItemCaptureModal: false, itemCaptureService: null, itemCaptureItems: [] })}
            service={itemCaptureService}
            items={itemCaptureItems}
            categoryConfig={categoryConfigs[itemCaptureService.service_category]}
            installerId={typeId}
            onComplete={this.handleItemCaptureComplete}
          />
        )}

        {/* Technician Notes Modal */}
        <Modal
          show={showNotesModal}
          onHide={this.handleHideNotes}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fa-sharp fa-regular fa-sticky-note mr-2"></i>
              Technician Notes
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {serviceForNotes && (
              <div>
                <div className="mb-3">
                  <label className="font-weight-bold">
                    Service: {serviceForNotes.service_category}
                    {serviceForNotes.custom_description && ` - ${serviceForNotes.custom_description}`}
                  </label>
                  {/* Show recommended product if available */}
                  {serviceForNotes.recommended_product_id && serviceForNotes.recommended_product_name && (
                    <div className="text-success mb-2">
                      <i className="fa-sharp fa-regular fa-check mr-1"></i>
                      <strong>Recommended Product:</strong>{' '}
                      {serviceForNotes.recommended_product_brand
                        ? `${serviceForNotes.recommended_product_brand} - ${serviceForNotes.recommended_product_name}`
                        : serviceForNotes.recommended_product_name}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Notes / Instructions</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={notesText}
                    onChange={(e) => this.handleNotesChange(e.target.value)}
                    placeholder="Add notes or instructions for this service..."
                  />
                  <small className="text-muted">
                    Add any notes about the service, materials used, issues encountered, etc.
                  </small>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={this.handleHideNotes}
              disabled={savingNotes}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={this.handleSaveNotes}
              disabled={savingNotes}
            >
              {savingNotes ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa-sharp fa-regular fa-save mr-2"></i>
                  Save Notes
                </>
              )}
            </button>
          </Modal.Footer>
        </Modal>

        {/* Materials View Modal - Shows collected materials for a service */}
        <Modal
          show={showMaterialsViewModal}
          onHide={this.handleHideMaterialsView}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fa-sharp fa-regular fa-cubes mr-2"></i>
              Materials Used
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {serviceForMaterialsView && (
              <div>
                <div className="mb-3">
                  <strong>{serviceForMaterialsView.service_category}</strong>
                  {serviceForMaterialsView.custom_description && (
                    <span className="text-muted"> - {serviceForMaterialsView.custom_description}</span>
                  )}
                </div>

                {serviceForMaterialsView.materials && serviceForMaterialsView.materials.length > 0 ? (
                  <div className="materials-list">
                    {serviceForMaterialsView.materials.map((material, index) => (
                      <div key={index} className="material-item p-2 mb-2 border rounded">
                        <div className="font-weight-bold">
                          {material.brand && <span>{material.brand} - </span>}
                          {material.product_name}
                        </div>
                        {material.quantity_used && (
                          <div className="text-muted small">
                            Quantity: {material.quantity_used} {material.unit_code || ''}
                          </div>
                        )}
                        {material.barcode && (
                          <div className="text-muted small">
                            Roll: <code>{material.barcode}</code>
                          </div>
                        )}
                        {material.lot_number && (
                          <div className="text-muted small">
                            Lot: <code>{material.lot_number}</code>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">No materials tracked.</p>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-secondary" onClick={this.handleHideMaterialsView}>
              Close
            </button>
          </Modal.Footer>
        </Modal>

        {/* Time Tracking Enforcement Modal */}
        <EnforcementModal
          isOpen={showEnforcementModal}
          onClose={this.handleCloseEnforcementModal}
          onCheckIn={this.handleEnforcementCheckIn}
          loading={enforcementCheckInLoading}
        />
      </div>
    );
  }
}

export default MyWork;
